import type { Creature } from '@/domain/creature/types';
import { deriveCareTraits } from './care-traits';
import { normalizeCareProfile } from './profile';
import { deriveTemperament, type TemperamentId } from './temperament';

export interface CareProfileView {
  temperament: TemperamentId;
  summaryKey: string;
  traitKeys: string[];
  tendencyKeys: string[];
  storyKeys: string[];
}

export function deriveCareProfileView(creature: Creature): CareProfileView {
  const profile = normalizeCareProfile(creature.careProfile);
  const temperament = deriveTemperament(creature.genome).primary;
  const traitKeys = deriveCareTraits(creature).map((trait) => `careProfile.trait.${trait}`);
  const tendencyKeys: string[] = [];
  const storyKeys: string[] = [];
  const meals = profile.healthyMeals + profile.heartyMeals;
  const interactions = meals + profile.snacks + profile.plays + profile.cleanups + profile.heals +
    profile.correctDisciplines + profile.wrongDisciplines;

  if (interactions === 0) {
    tendencyKeys.push('careProfile.tendency.discovering');
    storyKeys.push('careProfile.story.new');
  } else {
    if (meals > profile.snacks) {
      tendencyKeys.push('careProfile.tendency.balancedDiet');
      storyKeys.push('careProfile.story.nutritious');
    } else if (profile.snacks > meals) {
      tendencyKeys.push('careProfile.tendency.sweetTooth');
      storyKeys.push('careProfile.story.treats');
    }

    const dominantPlay = Math.max(profile.activePlays, profile.comfortPlays, profile.focusPlays);
    if (dominantPlay > 0) {
      if (profile.activePlays === dominantPlay) tendencyKeys.push('careProfile.tendency.activePlay');
      else if (profile.comfortPlays === dominantPlay) tendencyKeys.push('careProfile.tendency.comfortPlay');
      else tendencyKeys.push('careProfile.tendency.focusPlay');
    }
    if (profile.lateCleanups > 0) storyKeys.push('careProfile.story.lateCleaning');
    else if (profile.cleanups > 0) storyKeys.push('careProfile.story.cleanHome');
    if (profile.correctDisciplines > profile.wrongDisciplines) storyKeys.push('careProfile.story.goodDiscipline');
    if (profile.wrongDisciplines > 0 || profile.refusals > 0) storyKeys.push('careProfile.story.mistakes');
    if (profile.heals > 0) storyKeys.push('careProfile.story.recovered');
  }

  return {
    temperament,
    summaryKey: `careProfile.summary.${temperament}`,
    traitKeys,
    tendencyKeys,
    storyKeys,
  };
}
